import React, { useState } from "react";
import { ModalForm } from "collections/Modal";
import { DISMISS, useNotification } from "collections";
import { useHistory, useLocation, useRouteMatch } from "polyrouter";
import i18n, { LocalKeys, useTranslation } from "utilities/i18n";
import {
  AvailableExternalUserGroupsQuery,
  useImportUserGroupsMutation,
  useInviteMemberMutation,
  UserGroupSourceType
} from "generated/graphql";
import { FormikErrors, FormikHelpers } from "formik";
import * as yup from "yup";
import { InferType } from "yup";
import { InternalRoleNames } from "roles";
import "routes/Admin/Accounts/Members/InviteMemberModal.scss";
import { ImportLoginCheck } from "./ImportLoginCheck";
import { ImportGroupsForm } from "./ImportGroupsForm";
import { isValidInternalEmail } from "utilities/stringUtils";

enum InviteOptions {
  NOW = "NOW",
  LATER = "LATER"
}
// enumerates the possible pages in the group import workflow
class ImportPage {
  static readonly SELECT = new ImportPage(
    "SELECT",
    "IMPORT_USER_GROUPS_TITLE",
    "/select",
    "invite-member overflow"
  );
  static readonly OPTIONS = new ImportPage(
    "OPTIONS",
    "IMPORT_USER_GROUPS_OPTIONS_TITLE",
    "/options",
    ""
  );
  static readonly INVITE = new ImportPage(
    "INVITE",
    "IMPORT_USER_GROUPS_TITLE",
    "/invite",
    "invite-member overflow"
  );
  static readonly CONFIRM = new ImportPage(
    "CONFIRM",
    "IMPORT_USER_GROUPS_TITLE",
    "/confirm",
    ""
  );
  // private to disallow creating other instances of this type
  private constructor(
    private readonly key: string,
    public readonly titleKey: LocalKeys,
    public readonly nestedRoute: string,
    public readonly formSize: string
  ) {}

  toString() {
    return this.key;
  }
}

const pages = [
  ImportPage.SELECT,
  ImportPage.OPTIONS,
  ImportPage.INVITE,
  ImportPage.CONFIRM
];

const ImportGroupsFormSchema = yup.object({
  email: yup
    .string()
    .default("")
    .email(i18n.t("INVALID_EMAIL"))
    .label(i18n.t("EMAIL_ADDRESS"))
    .test("Email is in list", i18n.t("IS_IN_LIST"), function(email) {
      const { emails } = this.parent;
      if (emails.length) {
        const regex = new RegExp(emails.join("|"), "i"); // this adds case insensitivity to the list of emails
        return !regex.test(email);
      }
      return true;
    })
    .test(
      "Member is already on account",
      i18n.t("MEMBER_ALREADY_ON_ACCOUNT"),
      function(email) {
        const tenantMemberEmails = this.parent.tenantMemberEmails as string[];
        if (tenantMemberEmails.length) {
          const regex = new RegExp(tenantMemberEmails.join("|"), "i"); // this adds case insensitivity to the list of emails
          return !regex.test(email);
        } else {
          return true;
        }
      }
    )
    .test(
      "internal-email-required",
      i18n.t("INTERNAL_EMAIL_REQUIRED"),
      function(email) {
        return (
          !InternalRoleNames.includes(this.parent.role) ||
          isValidInternalEmail(email)
        );
      }
    ),
  emails: yup
    .array()
    .default([])
    .of(yup.string()),
  groups: yup
    .array()
    .default([])
    .of(yup.string()),
  tenantMemberEmails: yup
    .array()
    .of(yup.string())
    .default([])
});

type ImportGroupsFormProps = InferType<typeof ImportGroupsFormSchema>;

export const ImportGroupsModal: React.FC<{
  tenantId: string;
  returnUrl?: string;
}> = ({ tenantId, returnUrl }) => {
  const [selectedGroups, setSelectedGroups] = useState<Array<string>>([]);
  const [selectedOption, setSelectedOption] = useState<InviteOptions | null>(
    null
  );
  const [pendingEmails, setPendingEmails] = useState<Array<string>>([]);
  const [adGroupData, setAdGroupData] = useState<
    AvailableExternalUserGroupsQuery | undefined
  >(undefined);

  const [, sendInvite] = useInviteMemberMutation();
  const [addNotification] = useNotification();

  const [, importGroups] = useImportUserGroupsMutation();

  const sendEmails = async (
    emails: Array<string>,
    role: string,
    setErrors: (errors: FormikErrors<object & { email: string }>) => void
  ) => {
    const inviteRes = await sendInvite({
      emails: emails,
      tenant_id: tenantId,
      role: role
    });

    const inviteUser = inviteRes.data?.inviteUser;
    if (inviteUser) {
      //check that all emails were successfully sent
      if (inviteUser?.invites?.every((invite: any) => invite.success)) {
        addNotification({
          type: "success",
          message: t("EMAIL_SENT", { count: emails.length }),
          dismiss: DISMISS.DEFAULT,
          persistThroughNav: true
        });
        return;
      }

      inviteUser?.invites?.forEach(invite => {
        if (invite.error) {
          addNotification({
            type: "error",
            id: "error-" + invite.email,
            message: t("INVITATION_ERROR_TO", {
              email: invite.email,
              message: invite.error
            }),
            dismiss: DISMISS.DEFAULT,
            persistThroughNav: true
          });
        }
      });
    }

    setErrors({ email: `${t("TRY_AGAIN")}` });
    return;
  };

  const { t } = useTranslation();
  const { url } = useRouteMatch();
  const { pathname } = useLocation();

  const cancelURL = returnUrl ?? url.replace("/group/import", "");

  const currentPage =
    pages.find(page => pathname.includes(page.nestedRoute)) ??
    ImportPage.SELECT;

  const formTitle = t(currentPage.titleKey);
  const formSize = adGroupData?.userGroupsForTenant.length
    ? currentPage.formSize
    : "";

  const history = useHistory();
  const submit = async (
    values: ImportGroupsFormProps,
    formikHelpers: FormikHelpers<ImportGroupsFormProps>
  ) => {
    if (selectedOption === InviteOptions.NOW) {
      // send emails
      const emailsToSend: Array<string> =
        values.emails.length !== 0 ? values.emails : [];

      if (values.email) {
        await sendEmails(
          [...emailsToSend, values.email],
          "device_user",
          formikHelpers.setErrors
        );
      } else if (emailsToSend.length !== 0) {
        await sendEmails(emailsToSend, "device_user", formikHelpers.setErrors);
      } else {
        formikHelpers.resetForm();
      }
    }
    const groupsToImport = selectedGroups.filter(item => item !== "HEADER_ID");
    const { data, error } = await importGroups({
      tenantId,
      groups: groupsToImport.map(id => {
        const externalGroup = adGroupData?.userGroupsForTenant.find(
          group => group.id === id
        );
        return {
          id,
          name: externalGroup?.name
        };
      }),
      source: UserGroupSourceType.ActiveDirectory
    });

    if (error || data?.importUserGroups.length !== groupsToImport.length) {
      addNotification({
        type: "error",
        message: t("IMPORT_USER_GROUPS_FAILURE", {
          count: error
            ? selectedGroups.length
            : groupsToImport.length - (data?.importUserGroups?.length ?? 0)
        }),
        dismiss: DISMISS.DEFAULT,
        persistThroughNav: true
      });
    } else {
      addNotification({
        type: "success",
        message: t("IMPORT_USER_GROUPS_SUCCESS", {
          count: selectedGroups.filter(item => item !== "HEADER_ID").length
        }),
        dismiss: DISMISS.DEFAULT,
        persistThroughNav: true
      });
    }
    history.push(cancelURL);
  };

  return (
    <ModalForm
      title={formTitle}
      initialValues={{
        email: "",
        emails: pendingEmails,
        groups: selectedGroups.filter(item => item !== "HEADER_ID"),
        tenantMemberEmails: []
      }}
      onSubmit={submit}
      enableReinitialize
      name={"ImportGroupsModal"}
      validationSchema={ImportGroupsFormSchema}
      className={formSize}
    >
      {({
        isValid,
        dirty,
        values,
        isSubmitting,
        setFieldValue,
        setErrors,
        validateField
      }) => (
        <ImportLoginCheck cancelURL={cancelURL}>
          <ImportGroupsForm
            {...{
              isValid,
              dirty,
              values,
              isSubmitting,
              setFieldValue,
              setErrors,
              validateField,
              tenantId,
              selectedGroups,
              setSelectedGroups,
              pendingEmails,
              setPendingEmails,
              selectedOption,
              setSelectedOption,
              adGroupData,
              setAdGroupData
            }}
          />
        </ImportLoginCheck>
      )}
    </ModalForm>
  );
};
